import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ServiceBooking } from '@turizi/shared';
import { formatPrice } from '@/lib/format-utils';
import { BookingPaxsSize, ShowPaxGroup } from '@/components/snippets';
import { ServiceTitleStatic } from '@/components/services/service-title';
import { TagIcon } from '@/lib/tag-utils';
import { IconPackages } from '@tabler/icons-react';
import { useProducts } from '@/hooks/api-hooks';

interface BookingsExplorerSummaryProps {
    bookings: ServiceBooking[];
    displayOptions: {
        showPaxs?: boolean;
        showPrice?: boolean;
        showCost?: boolean;
        showByProduct?: boolean;
        showByService?: boolean;
        showByCategory?: boolean;
    };
}

interface GroupStat {
    key: string;
    title: string;
    category?: string;
    isProduct: boolean;
    serviceIds: number[];
    count: number;
    adults: number;
    children: number;
    seniors: number;
    infants: number;
    guests: number;
    totalPaxs: number;
    priceTotals: Record<string, number>;
    costTotals: Record<string, number>;
    profitTotals: Record<string, number>;
}

export default function BookingsExplorerSummary({ bookings, displayOptions }: BookingsExplorerSummaryProps) {
    const { t } = useTranslation();
    const showByProduct = displayOptions.showByProduct;

    const { data: productsData } = useProducts(undefined, { enabled: !!showByProduct });
    const products = productsData?.data || [];

    const serviceIdToProductMap = useMemo(() => {
        const map = new Map<number, string>();
        if (!showByProduct || !products.length) return map;

        products.forEach((prod) => {
            if (prod.name && Array.isArray(prod.services)) {
                prod.services.forEach((svc) => {
                    if (svc.id) {
                        map.set(svc.id, prod.name);
                    }
                });
            }
        });

        return map;
    }, [products, showByProduct]);

    const stats = useMemo(() => {
        if (!bookings || bookings.length === 0) return null;

        // General totals
        const totalBookings = bookings.length;
        const uniqueReservationIds = new Set(
            bookings
                .map((b) => b.reservation_id)
                .filter((id): id is number => id !== null && id !== undefined)
        );
        const totalReservations = uniqueReservationIds.size;

        const totalAdults = bookings.reduce((sum, b) => sum + (b.adults || 0), 0);
        const totalChildren = bookings.reduce((sum, b) => sum + (b.children || 0), 0);
        const totalSeniors = bookings.reduce((sum, b) => sum + (b.seniors || 0), 0);
        const totalInfants = bookings.reduce((sum, b) => sum + (b.infants || 0), 0);
        const totalGuests = bookings.reduce((sum, b) => sum + (b.guests || 0), 0);
        const totalPaxs = totalAdults + totalChildren + totalSeniors;

        // Financial totals (Price = Money In, Cost = Money Out)
        const costTotals = bookings.reduce((acc, b) => {
            const amt = Number(b.money_out_amount);
            const cur = b.money_out_currency;
            if (amt && cur) {
                acc[cur] = (acc[cur] || 0) + amt;
            }
            return acc;
        }, {} as Record<string, number>);

        const priceTotals = bookings.reduce((acc, b) => {
            const amt = Number(b.money_in_amount);
            const cur = b.money_in_currency;
            // original logic: exclude child bookings to prevent double invoicing in parent packages
            if (amt && cur && !b.parent_booking_id) {
                acc[cur] = (acc[cur] || 0) + amt;
            }
            return acc;
        }, {} as Record<string, number>);

        const allCurrencies = new Set([...Object.keys(priceTotals), ...Object.keys(costTotals)]);
        const profitTotals: Record<string, number> = {};
        allCurrencies.forEach((cur) => {
            const price = priceTotals[cur] || 0;
            const cost = costTotals[cur] || 0;
            profitTotals[cur] = price - cost;
        });

        // Grouping stats logic
        const groupMap = new Map<string, GroupStat>();

        bookings.forEach((b) => {
            const s = b.service_data || b.service;
            const sId = b.service_id || s?.id;

            let groupKey: string;
            let groupTitle: string;
            let isProduct = false;
            const category = s?.category || 'other';

            if (showByProduct) {
                const mappedProduct = sId ? serviceIdToProductMap.get(sId) : undefined;
                const prodName = mappedProduct || b.service_data?.product?.name || b.service?.product?.name;
                if (prodName) {
                    groupKey = `product_${prodName}`;
                    groupTitle = prodName;
                    isProduct = true;
                } else {
                    const staticTitle = ServiceTitleStatic(s);
                    groupKey = `service_${sId || staticTitle}`;
                    groupTitle = staticTitle || t('no_product');
                    isProduct = false;
                }
            } else {
                const staticTitle = ServiceTitleStatic(s);
                groupKey = `service_${sId || staticTitle}`;
                groupTitle = staticTitle || t('service');
                isProduct = false;
            }

            const bAdults = b.adults || 0;
            const bChildren = b.children || 0;
            const bSeniors = b.seniors || 0;
            const bInfants = b.infants || 0;
            const bGuests = b.guests || 0;
            const bPaxs = BookingPaxsSize(b);

            if (!groupMap.has(groupKey)) {
                groupMap.set(groupKey, {
                    key: groupKey,
                    title: groupTitle,
                    category,
                    isProduct,
                    serviceIds: [],
                    count: 0,
                    adults: 0,
                    children: 0,
                    seniors: 0,
                    infants: 0,
                    guests: 0,
                    totalPaxs: 0,
                    priceTotals: {},
                    costTotals: {},
                    profitTotals: {},
                });
            }

            const stat = groupMap.get(groupKey)!;
            if (sId && !stat.serviceIds.includes(sId)) {
                stat.serviceIds.push(sId);
            }
            stat.count += 1;
            stat.adults += bAdults;
            stat.children += bChildren;
            stat.seniors += bSeniors;
            stat.infants += bInfants;
            stat.guests += bGuests;
            stat.totalPaxs += bPaxs;

            // Accumulate price per group
            const pAmt = Number(b.money_in_amount);
            const pCur = b.money_in_currency;
            if (pAmt && pCur && !b.parent_booking_id) {
                stat.priceTotals[pCur] = (stat.priceTotals[pCur] || 0) + pAmt;
            }

            // Accumulate cost per group
            const cAmt = Number(b.money_out_amount);
            const cCur = b.money_out_currency;
            if (cAmt && cCur) {
                stat.costTotals[cCur] = (stat.costTotals[cCur] || 0) + cAmt;
            }
        });

        // Compute profit/margin per group
        groupMap.forEach((stat) => {
            const groupCurrencies = new Set([...Object.keys(stat.priceTotals), ...Object.keys(stat.costTotals)]);
            groupCurrencies.forEach((cur) => {
                const price = stat.priceTotals[cur] || 0;
                const cost = stat.costTotals[cur] || 0;
                stat.profitTotals[cur] = price - cost;
            });
        });

        const groupStatsList = Array.from(groupMap.values()).sort((a, b) => {
            if (b.totalPaxs !== a.totalPaxs) {
                return b.totalPaxs - a.totalPaxs;
            }
            return (a.title || '').localeCompare(b.title || '');
        });

        return {
            totalBookings,
            totalReservations,
            totalAdults,
            totalChildren,
            totalSeniors,
            totalInfants,
            totalGuests,
            totalPaxs,
            costTotals,
            priceTotals,
            profitTotals,
            groupStatsList,
        };
    }, [bookings, showByProduct, serviceIdToProductMap, t]);

    if (!stats) return null;

    const {
        totalAdults,
        totalChildren,
        totalSeniors,
        totalInfants,
        totalGuests,
        totalPaxs,
        costTotals,
        priceTotals,
        profitTotals,
        groupStatsList,
    } = stats;

    const showPrice = displayOptions.showPrice;
    const showCost = displayOptions.showCost;
    const showPaxs = displayOptions.showPaxs;

    // Check if we show net profit card
    const showNetProfit = showPrice && showCost;

    return (
        <div>
            {/* Main Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            </div>

            {/* Services / Products Breakdown Section */}
            <div>
                <div>
                    <div>
                        <div className='solid-box-border w-fit'>
                            <table className="text-xs">
                                <thead>
                                    <tr className="border-b border-slate-100 bg-slate-100/60 print:bg-slate-50 font-semibold text-slate-600">
                                        <th className="p-3 print:p-2">{showByProduct ? t('product') : t('service')}</th>
                                        <th className="p-3 print:p-2 text-center w-20">{t('bookings')}</th>
                                        {showPaxs && <th className="p-3 print:p-2 text-right">{t('paxs')}</th>}
                                        {showPrice && <th className="p-3 print:p-2 text-right w-36">{t('Revenue')}</th>}
                                        {showCost && <th className="p-3 print:p-2 text-right w-36">{t('costs')}</th>}
                                        {showNetProfit && <th className="p-3 print:p-2 text-right w-36">{t('Net Margin')}</th>}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {groupStatsList.map(({ key, title, category, isProduct, serviceIds, count, adults, children, seniors, infants, guests, priceTotals, costTotals, profitTotals }) => (
                                        <tr key={key} className="hover:bg-slate-100 transition-colors print:hover:bg-transparent">
                                            <td className="p-3 print:p-2">
                                                <div className="flex items-center gap-2">
                                                    {isProduct ? (
                                                        <IconPackages size={16} className="text-indigo-600 shrink-0" />
                                                    ) : (
                                                        <TagIcon
                                                            name={category}
                                                            noBackground
                                                            hideText
                                                            size="xxs"
                                                            className="shrink-0"
                                                        />
                                                    )}
                                                    <div>
                                                        <span className="font-medium text-slate-700">
                                                            {title}
                                                        </span>
                                                        {!isProduct && serviceIds.length > 0 && (
                                                            <span className="text-[10px] text-slate-400 ml-1.5">
                                                                #{serviceIds[0]}
                                                            </span>
                                                        )}
                                                        {isProduct && serviceIds.length > 0 && (
                                                            <span className="text-[10px] text-slate-400 ml-1.5">
                                                                ({serviceIds.map(id => `#${id}`).join(', ')})
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="p-3 print:p-2 text-right text-xxs text-slate-700">
                                                {count}
                                            </td>
                                            {showPaxs && (
                                                <td className="p-3 print:p-2">
                                                    <div className="flex items-center justify-end gap-1.5">
                                                        <ShowPaxGroup
                                                            adults={adults}
                                                            children={children}
                                                            seniors={seniors}
                                                            infants={infants}
                                                            guests={guests}
                                                            className="!bg-slate-100/80 !text-slate-700 !px-1.5 !py-0.5"
                                                        />
                                                    </div>
                                                </td>
                                            )}
                                            {showPrice && (
                                                <td className="p-3 print:p-2 text-right font-medium text-slate-700">
                                                    {Object.entries(priceTotals).map(([cur, val]) => (
                                                        <div key={cur} className="flex justify-end items-baseline gap-0.5">
                                                            <span className="text-[10px] text-slate-400">$</span>
                                                            <span>{formatPrice(val)}</span>
                                                            <span className="text-[9px] text-slate-400 uppercase ml-0.5">{cur}</span>
                                                        </div>
                                                    ))}
                                                    {Object.keys(priceTotals).length === 0 && <span className="text-slate-300">-</span>}
                                                </td>
                                            )}
                                            {showCost && (
                                                <td className="p-3 print:p-2 text-right font-medium text-slate-500">
                                                    {Object.entries(costTotals).map(([cur, val]) => (
                                                        <div key={cur} className="flex justify-end items-baseline gap-0.5">
                                                            <span className="text-[10px] text-slate-400">$</span>
                                                            <span>{formatPrice(val)}</span>
                                                            <span className="text-[9px] text-slate-400 uppercase ml-0.5">{cur}</span>
                                                        </div>
                                                    ))}
                                                    {Object.keys(costTotals).length === 0 && <span className="text-slate-300">-</span>}
                                                </td>
                                            )}
                                            {showNetProfit && (
                                                <td className="p-3 print:p-2 text-right font-semibold">
                                                    {Object.entries(profitTotals).map(([cur, val]) => {
                                                        const isNegative = val < 0;
                                                        return (
                                                            <div key={cur} className={`flex justify-end items-baseline gap-0.5 ${isNegative ? 'text-rose-600' : 'text-violet-700'}`}>
                                                                <span className="text-[10px] opacity-75">$</span>
                                                                <span>{formatPrice(val)}</span>
                                                                <span className="text-[9px] opacity-75 uppercase ml-0.5">{cur}</span>
                                                            </div>
                                                        );
                                                    })}
                                                    {Object.keys(profitTotals).length === 0 && <span className="text-slate-300">-</span>}
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
